import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Target } from 'lucide-react';
import { goalsApi, projectGoalsApi, type GoalMetric, type GoalSource } from './goalsApi';
import { queryKeys } from '@/lib/api/queryKeys';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';

/** Las cuatro métricas de Xotski. `samples` sólo se enseña si ya venía guardada. */
const METRICS: GoalMetric[] = ['responses', 'leads', 'prizesDelivered', 'completionRate'];

interface Row {
  metric: GoalMetric;
  value: number | null;
  source: GoalSource | null;
}

/**
 * CH-08 — El único sitio donde se FIJAN metas.
 *
 *   · `scope="project"`: las metas del proyecto. Cada evento las hereda
 *     enteras (no se reparten) salvo que fije la suya.
 *   · `scope="event"`: lo que rige en el evento, con su origen. Escribir un
 *     valor crea la meta propia (que pisa la del proyecto); «Usar la del
 *     proyecto» la quita y vuelve la heredada.
 *
 * Una fila por métrica, valor en un input, guardado al salir del campo o con
 * Enter. Sin cifra → sin meta: la métrica se sigue midiendo, sólo deja de
 * puntuarse. El progreso se enseña aparte (`GoalProgressCard`), en sólo lectura.
 */
export function GoalsEditor({
  scope,
  id,
  groupId,
  className,
}: {
  scope: 'event' | 'project';
  id: number;
  /** En alcance evento: su proyecto, para saber si detrás de una meta propia hay una heredable. */
  groupId?: number | null;
  className?: string;
}) {
  const t = useT();
  const queryClient = useQueryClient();

  const eventQ = useQuery({
    queryKey: queryKeys.goals.event(id),
    queryFn: () => goalsApi.list(id),
    enabled: scope === 'event' && id > 0,
  });
  const projectScopeId = scope === 'project' ? id : (groupId ?? 0);
  const projectQ = useQuery({
    queryKey: queryKeys.goals.project(projectScopeId),
    queryFn: () => projectGoalsApi.list(projectScopeId),
    enabled: projectScopeId > 0,
  });
  const projectHas = new Set((projectQ.data ?? []).map((g) => g.metric));
  const isLoading = scope === 'event' ? eventQ.isLoading : projectQ.isLoading;

  const rows: Row[] = (() => {
    const known = new Map<GoalMetric, Row>();
    if (scope === 'event') {
      for (const g of eventQ.data ?? []) known.set(g.metric, { metric: g.metric, value: Number(g.targetValue), source: g.source });
    } else {
      for (const g of projectQ.data ?? []) known.set(g.metric, { metric: g.metric, value: Number(g.targetValue), source: 'project' });
    }
    const metrics = [...METRICS, ...[...known.keys()].filter((m) => !METRICS.includes(m))];
    return metrics.map((m) => known.get(m) ?? { metric: m, value: null, source: null });
  })();

  const afterChange = () => {
    // Metas y progreso de cualquier evento del proyecto, más los hechos de
    // los dashboards (llevan la meta efectiva) y el monitor en directo.
    void queryClient.invalidateQueries({ queryKey: queryKeys.goals.all });
    void queryClient.invalidateQueries({ queryKey: ['insights'] });
    void queryClient.invalidateQueries({ queryKey: ['liveMonitor'] });
    void queryClient.invalidateQueries({ queryKey: ['eventReport'] });
  };

  const upsert = useMutation({
    mutationFn: ({ metric, value }: { metric: GoalMetric; value: number }) =>
      scope === 'event' ? goalsApi.upsert(id, metric, value) : projectGoalsApi.upsert(id, metric, value),
    onSuccess: () => {
      afterChange();
      toast({ tone: 'success', title: t('goals.editor.saved') });
    },
    onError: (e: Error) => toast({ tone: 'error', title: t('goals.editor.failed'), description: e.message }),
  });
  const remove = useMutation({
    mutationFn: (metric: GoalMetric) =>
      scope === 'event' ? goalsApi.remove(id, metric) : projectGoalsApi.remove(id, metric),
    onSuccess: () => {
      afterChange();
      toast({ tone: 'success', title: t('goals.editor.removed') });
    },
    onError: (e: Error) => toast({ tone: 'error', title: t('goals.editor.failed'), description: e.message }),
  });

  return (
    <section
      data-tour="goals-editor"
      className={cn('rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3', className)}
    >
      <div className="mb-1 flex items-center gap-2">
        <Target className="h-4 w-4 text-[var(--color-accent-ink)]" />
        <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          {t(scope === 'event' ? 'goals.editor.eventTitle' : 'goals.editor.projectTitle')}
        </h3>
      </div>
      <p className="mb-3 text-[11px] leading-snug text-[var(--color-fg-muted)]">
        {t(scope === 'event' ? 'goals.editor.eventHint' : 'goals.editor.projectHint')}
      </p>

      {isLoading ? (
        <p className="px-1 py-2 text-xs text-[var(--color-xk-text-muted)]">{t('common.loading')}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <GoalRow
              key={row.metric}
              scope={scope}
              row={row}
              fallback={scope === 'event' && projectHas.has(row.metric)}
              busy={upsert.isPending || remove.isPending}
              onSave={(value) => upsert.mutate({ metric: row.metric, value })}
              onRemove={() => remove.mutate(row.metric)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function GoalRow({
  scope,
  row,
  fallback,
  busy,
  onSave,
  onRemove,
}: {
  scope: 'event' | 'project';
  row: Row;
  /** Hay una meta del proyecto detrás: quitar la propia vuelve a ella. */
  fallback: boolean;
  busy: boolean;
  onSave: (value: number) => void;
  onRemove: () => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState(row.value != null ? String(row.value) : '');
  useEffect(() => {
    setDraft(row.value != null ? String(row.value) : '');
  }, [row.value]);

  const isPct = row.metric === 'completionRate';
  const own = scope === 'project' ? row.value != null : row.source === 'event';
  const inherited = scope === 'event' && row.source === 'project';

  const commit = () => {
    const txt = draft.trim();
    if (txt === '') {
      // Vacío: quitar la propia si la había. Una heredada no se quita desde aquí.
      if (own) onRemove();
      else setDraft(row.value != null ? String(row.value) : '');
      return;
    }
    const n = Number(txt.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0 || (isPct && n > 100)) {
      setDraft(row.value != null ? String(row.value) : '');
      return;
    }
    if (row.value != null && n === row.value && own) return;
    onSave(n);
  };

  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-2">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium">{t(`goals.metric.${row.metric}`)}</span>
          {own && scope === 'event' && (
            <span className="rounded-full bg-[var(--color-accent-10)] px-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--color-accent-ink)]">
              {t('goals.editor.own')}
            </span>
          )}
          {inherited && (
            <span className="rounded-full border border-[var(--color-border-default)] px-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--color-fg-muted)]">
              {t('goals.editor.inherited')}
            </span>
          )}
        </div>
        {inherited && (
          <p className="text-[11px] text-[var(--color-fg-subtle)]">{t('goals.editor.inheritedHint')}</p>
        )}
        {own && (
          <button
            type="button"
            disabled={busy}
            onClick={onRemove}
            className="text-[11px] font-semibold text-[var(--color-accent-ink)] hover:underline"
          >
            {fallback ? t('goals.editor.useProject') : t('goals.editor.remove')}
          </button>
        )}
      </div>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') setDraft(row.value != null ? String(row.value) : '');
          }}
          placeholder={t('goals.editor.none')}
          aria-label={t(`goals.metric.${row.metric}`)}
          className={cn(
            'h-9 w-28 rounded-lg border bg-[var(--color-bg-input)] px-2.5 text-right text-sm tabular-nums focus:border-[var(--color-accent-ink)] focus:outline-none',
            isPct && 'pr-6',
            inherited ? 'border-dashed border-[var(--color-border-default)] text-[var(--color-fg-muted)]' : 'border-[var(--color-border-default)] text-[var(--color-fg-primary)]',
          )}
        />
        {isPct && (
          <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-xs text-[var(--color-fg-subtle)]">%</span>
        )}
      </div>
    </li>
  );
}
